import React, {
  createContext, useContext, useCallback, useMemo, useState, useEffect
} from 'react';
import PropTypes from 'prop-types';
import {
  useFetchSweepstakes,
  useUpdateSweepstakes,
  useCompleteSweepstakesVideoStep,
} from '../hooks/fetch-sweepstakes';
import { isAdmin, isPurchaser } from '../common/utils/CardUtils';
import { isAuthenticated } from '../common/utils/AccountUtils';
import { LoyaltySweepstakesModel } from '../common/models/UserInfoModels';

const SweepstakesDataContext = createContext();

export const useSweepstakesData = () => {
  const context = useContext(SweepstakesDataContext);
  if (!context) {
    throw new Error('useSweepstakesData must be used within a SweepstakesDataProvider');
  }
  return context;
};

export const SweepstakesDataProvider = ({
  children, sweepstakesData: providedData, userOptInStatus, useRestAPI,
  forceAcceptSuccess, forceUnauthenticated, forcePurchaser
}) => {
  const effectivePurchaser = forcePurchaser || isPurchaser();
  const [step1Pending, setStep1Pending] = useState(false);

  // Fetch from API only if authenticated and not a purchaser
  const {
    challengeData, userProfileData, isLoading, hasError, isChallengeEnded, refresh
  } = useFetchSweepstakes({
    providedData,
    userOptInStatus,
    skip: !isAuthenticated() || effectivePurchaser,
    useRestAPI
  });
  // Use the update hook for managing runtime data changes
  const { effectiveData, updateSweepstakesData } = useUpdateSweepstakes(challengeData);
  const {
    completeSweepstakesVideoStep,
    isLoadingCompleteVideo,
    completeVideoError,
  } = useCompleteSweepstakesVideoStep();
  const isFirstStepCompleted = Boolean(effectiveData?.steps?.[0]?.completed);

  // Update effective data manually during opt-in flow
  const handleOptIn = useCallback(() => {
    setStep1Pending(true);
    updateSweepstakesData({
      isOptedIn: true,
      activityStatus: 'OPTED_IN',
      completedSteps: 0,
    });
  }, [updateSweepstakesData]);

  const clearStep1Pending = useCallback(() => {
    setStep1Pending(false);
  }, []);

  useEffect(() => {
    if (!effectiveData?.isOptedIn) {
      setStep1Pending(false);
      return;
    }

    const hasCompletedFirstStep = Boolean(
      isFirstStepCompleted || (effectiveData?.completedSteps || 0) > 0
    );

    if (hasCompletedFirstStep) {
      setStep1Pending(false);
    }
  }, [effectiveData?.isOptedIn, effectiveData?.completedSteps, isFirstStepCompleted]);

  const mergedSweepstakesData = useMemo(() => {
    if (!effectiveData) return effectiveData;

    return {
      ...effectiveData,
      step1Pending,
      isOptedIn: effectiveData.isOptedIn || step1Pending,
      activityStatus: step1Pending ? 'OPTED_IN' : effectiveData.activityStatus,
    };
  }, [effectiveData, step1Pending]);

  const contextValue = useMemo(() => ({
    sweepstakesData: mergedSweepstakesData,
    userProfileData,
    isLoading,
    hasError,
    isChallengeEnded,
    refresh,
    updateSweepstakesData,
    completeSweepstakesVideoStep,
    isLoadingCompleteVideo,
    completeVideoError,
    forceAcceptSuccess,
    forceUnauthenticated,
    handleOptIn,
    clearStep1Pending,
    isAdmin: forcePurchaser ? false : isAdmin(),
    isPurchaser: effectivePurchaser,
  }), [
    mergedSweepstakesData,
    userProfileData,
    isLoading,
    hasError,
    isChallengeEnded,
    refresh,
    updateSweepstakesData,
    completeSweepstakesVideoStep,
    isLoadingCompleteVideo,
    completeVideoError,
    forceAcceptSuccess,
    forceUnauthenticated,
    forcePurchaser,
    handleOptIn,
    clearStep1Pending,
  ]);

  return (
    <SweepstakesDataContext.Provider value={contextValue}>
      {children}
    </SweepstakesDataContext.Provider>
  );
};

SweepstakesDataProvider.displayName = 'Sweepstakes Data Provider';
SweepstakesDataProvider.dataModel = LoyaltySweepstakesModel;

SweepstakesDataProvider.propTypes = {
  children: PropTypes.node.isRequired,
  forceAcceptSuccess: PropTypes.bool,
  forcePurchaser: PropTypes.bool,
  forceUnauthenticated: PropTypes.bool,
  sweepstakesData: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    logo: PropTypes.string,
    status: PropTypes.string,
    expirationDate: PropTypes.string,
    isOptedIn: PropTypes.bool,
    completedSteps: PropTypes.number,
    steps: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      iconUrl: PropTypes.string,
      completed: PropTypes.bool,
    })),
  }),
  userOptInStatus: PropTypes.oneOf(['notOptedIn', 'partiallyCompleted', 'fullyCompleted']),
};

SweepstakesDataProvider.defaultProps = {
  sweepstakesData: null,
  userOptInStatus: 'notOptedIn',
  forceAcceptSuccess: false,
  forcePurchaser: false,
  forceUnauthenticated: false,
};
